/**
 * BottomSheet Component - Enhanced Version
 * Mobile-friendly bottom sheet/drawer with smooth animations
 * Features:
 * - Spring-based animations with velocity tracking
 * - Smart snap points with momentum
 * - Touch gesture handling with rubber band effect
 * - Safe area support for iOS
 */
import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import clsx from 'clsx'

// Snap points as percentage of viewport height
// Note: Header height = 56px, BottomNav = 64px
// Available space = 100vh - header - bottomnav ≈ 100vh - 120px
const DEFAULT_SNAP_POINTS = {
  collapsed: 12,   // Show handle + peek content (~12vh) - shorter due to bottomnav
  half: 45,        // Half of available space
  expanded: 70,    // Almost full available space (không che header và nằm trên bottomnav)
}

// Animation constants
const SPRING_CONFIG = {
  tension: 300,
  friction: 30,
  mass: 1,
}

// Velocity threshold for momentum-based snap
const VELOCITY_THRESHOLD = 0.5 // vh/ms
const MIN_DRAG_DISTANCE = 10 // px

function BottomSheet({
  children,
  isOpen = true,
  onClose,
  snapPoints = DEFAULT_SNAP_POINTS,
  defaultSnap = 'collapsed',
  peekContent,
  title,
  className,
  onSnapChange,
}) {
  const [currentSnap, setCurrentSnap] = useState(defaultSnap)
  const [isDragging, setIsDragging] = useState(false)
  const [dragY, setDragY] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)

  const sheetRef = useRef(null)
  const startY = useRef(0)
  const startHeight = useRef(0)
  const lastY = useRef(0)
  const lastTime = useRef(0)
  const velocity = useRef(0)
  const animationRef = useRef(null)

  // Memoized snap heights
  const snapHeights = useMemo(() => ({
    collapsed: snapPoints.collapsed,
    half: snapPoints.half,
    expanded: snapPoints.expanded,
  }), [snapPoints])

  // Get height based on snap point
  const getSnapHeight = useCallback((snap) => {
    return snapHeights[snap] || snapHeights.collapsed
  }, [snapHeights])

  // Current visual height
  const currentHeight = useMemo(() => {
    const baseHeight = getSnapHeight(currentSnap)
    return Math.max(8, Math.min(95, baseHeight + dragY))
  }, [currentSnap, dragY, getSnapHeight])

  // Spring animation to target height
  const animateToHeight = useCallback((targetSnap) => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current)
    }

    const startH = currentHeight
    const targetH = getSnapHeight(targetSnap)
    const startTime = performance.now()
    const duration = 350 // ms

    setIsAnimating(true)

    const animate = (time) => {
      const elapsed = time - startTime
      const progress = Math.min(elapsed / duration, 1)

      // Easing: ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3)

      const newHeight = startH + (targetH - startH) * eased
      setDragY(newHeight - getSnapHeight(currentSnap))

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate)
      } else {
        setCurrentSnap(targetSnap)
        setDragY(0)
        setIsAnimating(false)
        onSnapChange?.(targetSnap)
      }
    }

    animationRef.current = requestAnimationFrame(animate)
  }, [currentHeight, currentSnap, getSnapHeight, onSnapChange])

  // Handle drag start
  const handleDragStart = useCallback((e) => {
    if (isAnimating) return

    const clientY = e.touches ? e.touches[0].clientY : e.clientY
    startY.current = clientY
    startHeight.current = getSnapHeight(currentSnap)
    lastY.current = clientY
    lastTime.current = performance.now()
    velocity.current = 0

    setIsDragging(true)
  }, [currentSnap, getSnapHeight, isAnimating])

  // Handle drag move
  const handleDragMove = useCallback((e) => {
    if (!isDragging) return

    const clientY = e.touches ? e.touches[0].clientY : e.clientY
    const now = performance.now()
    const deltaTime = now - lastTime.current

    // Calculate velocity (vh/ms)
    if (deltaTime > 0) {
      const deltaY = lastY.current - clientY
      const deltaVh = (deltaY / window.innerHeight) * 100
      velocity.current = deltaVh / deltaTime
    }

    lastY.current = clientY
    lastTime.current = now

    // Calculate drag offset
    const delta = startY.current - clientY
    const deltaPercent = (delta / window.innerHeight) * 100

    // Apply rubber band effect at boundaries
    let adjustedDelta = deltaPercent
    const newHeight = startHeight.current + deltaPercent

    if (newHeight > snapHeights.expanded) {
      const overshoot = newHeight - snapHeights.expanded
      adjustedDelta = deltaPercent - overshoot * 0.7 // Rubber band
    } else if (newHeight < snapHeights.collapsed - 10) {
      const undershoot = snapHeights.collapsed - 10 - newHeight
      adjustedDelta = deltaPercent + undershoot * 0.7 // Rubber band
    }

    setDragY(adjustedDelta)
  }, [isDragging, snapHeights])

  // Handle drag end with momentum
  const handleDragEnd = useCallback(() => {
    if (!isDragging) return

    setIsDragging(false)

    const finalHeight = currentHeight
    const v = velocity.current

    // Determine target snap based on velocity and position
    let targetSnap = currentSnap

    // If significant velocity, use momentum
    if (Math.abs(v) > VELOCITY_THRESHOLD) {
      if (v > 0) {
        // Swiping up - go to higher snap
        const snapOrder = ['collapsed', 'half', 'expanded']
        const currentIndex = snapOrder.indexOf(currentSnap)
        if (currentIndex < snapOrder.length - 1) {
          targetSnap = snapOrder[currentIndex + 1]
        }
      } else {
        // Swiping down - go to lower snap
        const snapOrder = ['collapsed', 'half', 'expanded']
        const currentIndex = snapOrder.indexOf(currentSnap)
        if (currentIndex > 0) {
          targetSnap = snapOrder[currentIndex - 1]
        } else if (finalHeight < snapHeights.collapsed - 5 && onClose) {
          // Close if dragged below collapsed
          onClose()
          setDragY(0)
          return
        }
      }
    } else {
      // No significant velocity - snap to nearest
      const snapEntries = Object.entries(snapHeights)
      let minDiff = Infinity

      snapEntries.forEach(([key, value]) => {
        const diff = Math.abs(value - finalHeight)
        if (diff < minDiff) {
          minDiff = diff
          targetSnap = key
        }
      })
    }

    // Animate to target
    if (targetSnap !== currentSnap || Math.abs(dragY) > 0.1) {
      animateToHeight(targetSnap)
    } else {
      setDragY(0)
    }
  }, [isDragging, currentHeight, currentSnap, snapHeights, dragY, velocity, animateToHeight, onClose])

  // Global event listeners for drag
  useEffect(() => {
    if (!isDragging) return

    const handleMove = (e) => {
      e.preventDefault()
      handleDragMove(e)
    }

    window.addEventListener('mousemove', handleDragMove)
    window.addEventListener('mouseup', handleDragEnd)
    window.addEventListener('touchmove', handleMove, { passive: false })
    window.addEventListener('touchend', handleDragEnd)

    return () => {
      window.removeEventListener('mousemove', handleDragMove)
      window.removeEventListener('mouseup', handleDragEnd)
      window.removeEventListener('touchmove', handleMove)
      window.removeEventListener('touchend', handleDragEnd)
    }
  }, [isDragging, handleDragMove, handleDragEnd])

  // Clean up animation on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [])

  // Reset snap when defaultSnap changes
  useEffect(() => {
    setCurrentSnap(defaultSnap)
    setDragY(0)
  }, [defaultSnap])

  // Toggle between collapsed and half
  const handleToggle = useCallback(() => {
    const nextSnap = currentSnap === 'collapsed' ? 'half' : 'collapsed'
    animateToHeight(nextSnap)
  }, [currentSnap, animateToHeight])

  // Expand to full
  const handleExpand = useCallback(() => {
    animateToHeight('expanded')
  }, [animateToHeight])

  // Collapse
  const handleCollapse = useCallback(() => {
    animateToHeight('collapsed')
  }, [animateToHeight])

  if (!isOpen) return null

  const isCollapsed = currentSnap === 'collapsed' && Math.abs(dragY) < 5
  const isExpanded = currentSnap === 'expanded' && Math.abs(dragY) < 5

  return (
    <>
      {/* Backdrop - show when not collapsed, below BottomNav */}
      <div
        className={clsx(
          'fixed inset-0 bg-black z-20 md:hidden transition-opacity duration-300',
          !isCollapsed ? 'opacity-40' : 'opacity-0 pointer-events-none'
        )}
        style={{
          // Stop backdrop above BottomNav
          bottom: '64px',
        }}
        onClick={handleCollapse}
      />

      {/* Sheet - positioned above BottomNav (64px height) */}
      <div
        ref={sheetRef}
        className={clsx(
          'fixed inset-x-0 z-30 md:hidden',
          'bg-white rounded-t-3xl shadow-2xl',
          'will-change-[height]',
          !isDragging && !isAnimating && 'transition-[height] duration-300 ease-out',
          className
        )}
        style={{
          height: `${currentHeight}vh`,
          // Position above BottomNav (h-16 = 64px)
          bottom: '64px',
          // Đảm bảo không bao giờ vượt quá header (64px + 8px margin + 64px bottomnav)
          maxHeight: 'calc(100vh - 136px)',
          // Safe area padding for iOS
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        {/* Drag Handle Area */}
        <div
          className={clsx(
            'flex flex-col items-center pt-3 pb-2 touch-none select-none',
            'cursor-grab active:cursor-grabbing'
          )}
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
        >
          {/* Handle bar */}
          <div className={clsx(
            'w-12 h-1.5 rounded-full transition-colors duration-200',
            isDragging ? 'bg-gray-400' : 'bg-gray-300'
          )} />

          {/* Quick action hint */}
          {isCollapsed && (
            <div
              className="mt-2 text-xs text-gray-400 animate-pulse"
              onClick={handleToggle}
            >
              Vuốt lên để xem danh sách
            </div>
          )}
        </div>

        {/* Header */}
        {title && !isCollapsed && (
          <div className="px-4 pb-2 flex items-center justify-between border-b border-gray-100">
            <h3 className="font-semibold text-gray-900 truncate">{title}</h3>
            <div className="flex items-center gap-2">
              {currentSnap === 'half' && (
                <button
                  onClick={handleExpand}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Mở rộng"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                </button>
              )}
              {currentSnap === 'expanded' && (
                <button
                  onClick={handleCollapse}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Thu nhỏ"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Peek Content - Show when collapsed */}
        {isCollapsed && peekContent && (
          <div
            className="px-4 py-3 cursor-pointer active:bg-gray-50 transition-colors"
            onClick={handleToggle}
          >
            {peekContent}
          </div>
        )}

        {/* Main Content - scrollable */}
        <div
          className={clsx(
            'overflow-auto overscroll-contain',
            'scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent',
            isCollapsed ? 'hidden' : 'block'
          )}
          style={{
            height: `calc(100% - ${title ? '80px' : '56px'})`,
          }}
        >
          {children}
        </div>

        {/* Bottom safe area spacer */}
        <div className="h-safe-bottom" />
      </div>
    </>
  )
}

export default BottomSheet
